/**
 * Drive the bulk endpoints over many rows: chunk, send, map each result's
 * `index` back to its row, and turn a failed request into a failed outcome
 * for every row in it (the rest of the run carries on).
 *
 * The API is injected so this runs in tests without React or fetch.
 */
import type {
  BulkCheckResponse,
  BulkItem,
  BulkLogItem,
  BulkLogResponse,
  BulkMessagedResponse,
} from '@/api/types';
import {
  CHECK_CHUNK,
  LOG_CHUNK,
  LOG_CHUNK_BYTES,
  LOG_CONCURRENCY,
  MESSAGED_CHUNK,
  chunk,
  chunkByWeight,
  runPool,
} from './batch';
import { outcomeMessage, type RowOutcome } from './copy';
import type { RowCheck } from './rows';

export interface BulkTransport {
  check: (items: BulkItem[]) => Promise<BulkCheckResponse>;
  messaged: (items: Array<BulkItem & { note?: string }>) => Promise<BulkMessagedResponse>;
  log: (items: BulkLogItem[], files: Map<string, File>) => Promise<BulkLogResponse>;
}

/** Thrown when the server says the caller may not bulk import. Ends the run. */
export class BulkNotAllowedError extends Error {
  constructor() {
    super('Bulk import is not turned on for you');
    this.name = 'BulkNotAllowedError';
  }
}

export interface RunControl {
  isNotAllowed: (err: unknown) => boolean;
  shouldStop?: () => boolean;
  /** Called after each chunk with rows done so far and the total. */
  onProgress?: (done: number, total: number) => void;
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : 'Request failed';
}

/** Check creators with the server. Keys in the result are the callers' row keys. */
export async function runCheck(
  transport: BulkTransport,
  items: ReadonlyArray<BulkItem & { key: number }>,
  ctl: RunControl,
): Promise<Map<number, RowCheck>> {
  const out = new Map<number, RowCheck>();
  let done = 0;
  for (const part of chunk(items, CHECK_CHUNK)) {
    if (ctl.shouldStop?.()) break;
    let res: BulkCheckResponse;
    try {
      res = await transport.check(part.map(({ channel, targetIdentifier }) => ({ channel, targetIdentifier })));
    } catch (err) {
      if (ctl.isNotAllowed(err)) throw new BulkNotAllowedError();
      throw err;
    }
    for (const r of res.results) {
      const item = part[r.index];
      if (!item) continue;
      out.set(item.key, { status: r.status, inviteStage: r.inviteStage, heldByName: r.heldByName, reason: r.reason });
    }
    done += part.length;
    ctl.onProgress?.(done, items.length);
  }
  return out;
}

export interface MessagedTask extends BulkItem {
  key: number;
  note?: string;
}

/** Mark creators as messaged, 500 per request, one request at a time. */
export async function runMessaged(
  transport: BulkTransport,
  tasks: ReadonlyArray<MessagedTask>,
  onOutcome: (key: number, outcome: RowOutcome) => void,
  ctl: RunControl,
): Promise<void> {
  let done = 0;
  for (const part of chunk(tasks, MESSAGED_CHUNK)) {
    if (ctl.shouldStop?.()) break;
    try {
      const res = await transport.messaged(
        part.map(({ channel, targetIdentifier, note }) => (note ? { channel, targetIdentifier, note } : { channel, targetIdentifier })),
      );
      const seen = new Set<number>();
      for (const r of res.results) {
        const t = part[r.index];
        if (!t) continue;
        seen.add(r.index);
        onOutcome(t.key, r.ok ? { ok: true } : { ok: false, code: r.code, message: outcomeMessage(r.code, r.message) });
      }
      part.forEach((t, i) => {
        if (!seen.has(i)) onOutcome(t.key, { ok: false, message: 'No answer from the server for this row' });
      });
    } catch (err) {
      if (ctl.isNotAllowed(err)) throw new BulkNotAllowedError();
      for (const t of part) onOutcome(t.key, { ok: false, message: errorText(err) });
    }
    done += part.length;
    ctl.onProgress?.(done, tasks.length);
  }
}

export interface LogTask {
  key: number;
  item: Omit<BulkLogItem, 'screenshotField'>;
  screenshot: File | null;
}

/**
 * Log replies: at most 25 rows and ~20 MB of screenshots per request, two
 * requests in flight.
 */
export async function runLog(
  transport: BulkTransport,
  tasks: ReadonlyArray<LogTask>,
  onOutcome: (key: number, outcome: RowOutcome) => void,
  ctl: RunControl,
): Promise<void> {
  const parts = chunkByWeight(tasks, LOG_CHUNK, LOG_CHUNK_BYTES, (t) => t.screenshot?.size ?? 0);
  let done = 0;
  let notAllowed = false;
  await runPool(
    parts,
    LOG_CONCURRENCY,
    async (part) => {
      const files = new Map<string, File>();
      const items: BulkLogItem[] = part.map((t, i) => {
        if (!t.screenshot) return { ...t.item };
        const field = `screenshot_${i}`;
        files.set(field, t.screenshot);
        return { ...t.item, screenshotField: field };
      });
      try {
        const res = await transport.log(items, files);
        const seen = new Set<number>();
        for (const r of res.results) {
          const t = part[r.index];
          if (!t) continue;
          seen.add(r.index);
          if (r.ok) {
            onOutcome(t.key, { ok: true, invitePublicId: r.invitePublicId });
          } else if (r.invitePublicId) {
            // The invite exists (e.g. SCREENSHOT_UPLOAD_FAILED): a success
            // with a warning, so "Retry failed" never logs it twice.
            onOutcome(t.key, { ok: true, invitePublicId: r.invitePublicId, code: r.code, message: outcomeMessage(r.code, r.message) });
          } else {
            onOutcome(t.key, { ok: false, code: r.code, message: outcomeMessage(r.code, r.message) });
          }
        }
        part.forEach((t, i) => {
          if (!seen.has(i)) onOutcome(t.key, { ok: false, message: 'No answer from the server for this row' });
        });
      } catch (err) {
        if (ctl.isNotAllowed(err)) {
          notAllowed = true;
          return;
        }
        for (const t of part) onOutcome(t.key, { ok: false, message: errorText(err) });
      }
      done += part.length;
      ctl.onProgress?.(done, tasks.length);
    },
    () => notAllowed || (ctl.shouldStop?.() ?? false),
  );
  if (notAllowed) throw new BulkNotAllowedError();
}
